import { CartOutput, EventSeatOutput, EventSectionOutput, EventTypeOutput } from "@brushfire/types";
import React, { useContext, useEffect, useState } from "react";
import { groupBy } from "lodash";
import { Icon } from "..";

export interface ASTSectionRowProps {
	seat: EventSeatOutput;
	section: EventSectionOutput;
	selectedSeats: string[];
	onSelectSeats: (seats: EventSeatOutput[], target: null | HTMLElement) => void;
	isAdmin?: boolean;
	cart?: CartOutput;
	loadingSeats: string[];
	priceRange?: number[];
	isInclusiveFees: boolean;
	attendeeTypeData: EventTypeOutput[];
}

const seatState = (
	inaccessibleDueToAccessCode: boolean,
	seatType: string,
	stageTop: boolean,
	isWheelChair: boolean
) => {
	if (!isWheelChair) {
		if (inaccessibleDueToAccessCode && seatType === "Seat" && stageTop) {
			return 0;
		} else if (inaccessibleDueToAccessCode && seatType === "Seat" && !stageTop) {
			return 1;
		} else if (inaccessibleDueToAccessCode && seatType === "Pew" && stageTop) {
			return 2;
		} else if (inaccessibleDueToAccessCode && seatType === "Pew" && !stageTop) {
			return 3;
		} else if (inaccessibleDueToAccessCode && seatType === "Spot") {
			return 5;
		} else if (!inaccessibleDueToAccessCode && seatType === "Seat" && stageTop) {
			return 6;
		} else if (!inaccessibleDueToAccessCode && seatType === "Seat" && !stageTop) {
			return 7;
		} else if (!inaccessibleDueToAccessCode && seatType === "Pew" && stageTop) {
			return 8;
		} else if (!inaccessibleDueToAccessCode && seatType === "Pew" && !stageTop) {
			return 9;
		} else if (!inaccessibleDueToAccessCode && seatType === "Spot") {
			return 11;
		}
	} else {
		if (inaccessibleDueToAccessCode) {
			return 4;
		} else {
			return 10;
		}
	}
	return 0;
};

const ASTSectionRow: React.FC<ASTSectionRowProps> = ({
	seat,
	section,
	isAdmin,
	selectedSeats,
	onSelectSeats,
	cart,
	loadingSeats,
	priceRange,
	isInclusiveFees,
	attendeeTypeData,
}) => {
	if (!seat || !section) {
		return null;
	}

	const checkForOneOffs = (stateId: number, seatStateId: number, isMannedMode: boolean, seatAvailable: boolean) => {
		if (isMannedMode) {
			if (stateId === 0 && seatStateId === 2) {
				return "seat_6_12";
			} else if (stateId === 0 && seatStateId === 3) {
				return "seat_6_13";
			} else if (stateId === 0 && seatStateId === 0) {
				return "seat_4_12";
			} else if (stateId === 0 && seatStateId === 1) {
				return "seat_4_13";
			} else if (stateId === 0 && seatStateId === 5) {
				return "seat_7_12";
			}
		} else if (!seatAvailable) {
			if (stateId === 0 && seatStateId === 2) {
				return "seatunavailable_8";
			}
			if (stateId === 0 && seatStateId === 3) {
				return "seatunavailable_9";
			}
			if (stateId === 0 && seatStateId === 0) {
				return "seatunavailable_6";
			}
			if (stateId === 0 && seatStateId === 1) {
				return "seatunavailable_7";
			}
			if (stateId === 0 && seatStateId === 5) {
				return "seatunavailable_11";
			}
		}
		return null;
	};

	const getSeatClass = (seat: EventSeatOutput) => {
		const { seatType, inaccessibleDueToAccessCode, wheelchair, state, available } = seat;

		const stageTop = section.isRowAscending;

		if (state.id !== 5) {
			let seatStateInt = seatState(inaccessibleDueToAccessCode, seatType, stageTop, wheelchair);
			if (!isAdmin) {
				if (available === false) {
					let oneOff = checkForOneOffs(state.id, seatStateInt, false, false);
					if (oneOff) {
						return oneOff;
					}
					return `seatunavailable_${seatStateInt}`;
				}
				if (wheelchair === true) {
					return `seat_0_${seatStateInt}`;
				}
				return `seat_${state.id}_${seatStateInt}`;
			} else {
				const oneOff = checkForOneOffs(state.id, seatStateInt, true, false);
				if (oneOff) {
					return oneOff;
				}
				if (wheelchair === true) {
					return `seat_0_${seatStateInt}`;
				}
				return `seat_${state.id}_${seatStateInt}`;
			}
		}
		return null;
	};

	const seatClass = getSeatClass(seat);

	const isLoading = loadingSeats.includes(seat.seatId);
	const mySeat = !!cart?.attendees?.find((a) => a.seatId === seat.seatId);

	const targetTypes = attendeeTypeData?.filter((a) => seat?.types.includes(a.id));
	const seatAmounts: number[] = [];
	targetTypes?.forEach((type) => {
		type.amount = type.prices.find((p) => p.basePrice === seat?.unitPrice)?.amount ?? null;
		type.fee = type.prices.find((p) => p.basePrice === seat?.unitPrice)?.fee ?? null;
		seatAmounts.push((type.amount ?? 0) + (isInclusiveFees ? type.fee ?? 0 : 0));
	});

	return (
		<>
			<div
				className="ast-seat-block"
				style={{ backgroundColor: seatClass ? `rgba(${seat.priceColor.rgb},0.55)` : "" }}
			>
				{seatClass || isAdmin ? (
					<div
						onClick={
							!seat.available && !mySeat && !seat.wheelchair
								? undefined
								: (evt) => {
										onSelectSeats([seat], evt.currentTarget);
								  }
						}
						style={{
							position: "relative",
							alignItems: "center",
							justifyContent: "center",
							backgroundColor: isAdmin ? `rgba(${seat.state.backColor.rgb}, 0.8)` : undefined,
							color: isAdmin ? `rgb(${seat.state.foreColor.rgb})` : undefined,
						}}
						className={`seat ${mySeat ? "my-seat" : ""} ${
							selectedSeats.includes(seat.seatId) ? "selected" : ""
						} ${seatClass}${
							!seat.available && !mySeat && !(seat.wheelchair && seat.state.id === 4)
								? " unavailable"
								: ""
						} ${seat.inaccessibleDueToAccessCode ? (isAdmin ? "locked" : "unavailable") : ""} ${
							priceRange &&
							seatAmounts.every((amount) => amount < priceRange[0] || amount > priceRange[1])
								? "filtered"
								: ""
						}`}
					>
						<div>
							{!seat.wheelchair && !seat.limitedView && !seat.distanced && !isLoading && (
								<span>{seat.seatLabel}</span>
							)}

							{seat.inaccessibleDueToAccessCode && isAdmin && <Icon classes="locked" icon={"lock"} />}
							{isLoading ? (
								<Icon icon={"spinner-third"} classes="fa-spin" />
							) : (
								<>
									{seat.distanced ? (
										<Icon icon={"people-arrows"} />
									) : (
										<>
											{seat.limitedView && !seat.wheelchair && <Icon icon={"info-circle"} />}
											{seat.wheelchair && <Icon icon={"wheelchair"} />}
										</>
									)}
								</>
							)}
						</div>
					</div>
				) : (
					<div className="seat empty-seat"></div>
				)}
			</div>
		</>
	);
};

export default ASTSectionRow;
